// imports
import React, { useCallback, useEffect, useState } from 'react';
import RoomIcon from '@mui/icons-material/Room';
import { Box, Button, CircularProgress, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
// components
import { Alert } from '../../../../shared/lib/common/Alert';
import { BUTTON_TEXT_VIEW, BUTTON_TEXT_VIEW_ON_MAP, LABEL_CALL_CHARGES, LABEL_CALL_STATUS, LABEL_DATE_AND_TIME, LABEL_LOCATION, LABEL_NOT_AVAILABLE, LABEL_NOTIFICATION_TYPE, LABEL_RING_MY_PHONE_LOGS, LABEL_STATUS, MSG_NO_RMP_LOGS_FOUND } from '../../../../shared/lib/constants';
import { withTrademark } from '../../../../shared/lib/common/Tokenizer';
import { CustomPagination } from '../common/CustomPagination';
import { StickyTable } from '../../../../shared/lib/common/StickyTable';
//  hooks
import { useAppContext } from '../../app/contexts/AppContext';
import { ActionType } from '../../app/reducer/AppContextReducer';
// styles
import palette from '../../../../shared/lib/theme/palette';
import { userDetailsWrapper } from '../../../../shared/lib/theme/styleConstants';
import { TransactionsHistoryChipStyle } from '../../../../shared/lib/theme/styleComponents';
// graphql
import { RingMyPhoneLogs as RingMyPhoneLogsType, RingMyPhoneStatus, useRecentRingMyPhoneLogsLazyQuery } from '../../generated';
import LocationModal from '../dashboard/LocationModal';
import CardLockComponents from './CardLockComponents';

const RingMyPhoneLogs = () => {
  const { blackTwo, blackOne, greenOne, red, blue } = palette;

  const { dispatch: appDispatch, currentPage, selectedPlan } = useAppContext();

  const [logs, setLogs] = useState<RingMyPhoneLogsType[]>([]);
  const [open, setOpen] = useState<boolean>(false);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const [getLogs, { loading: logLoading }] = useRecentRingMyPhoneLogsLazyQuery({
    fetchPolicy: 'network-only',

    onCompleted: (data) => {
      const { recentRingMyPhoneLogs } = data;
      const { pagination, ringMyPhoneLogs } = recentRingMyPhoneLogs || {};
      setLogs(ringMyPhoneLogs || []);
      appDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: pagination?.totalPages || 0 });
    },

    onError: ({ message }) => {
      Alert.error(message);
      appDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: 0 });
    }
  });

  const fetchLogs = useCallback(async () => {
    await getLogs({
      variables: {
        input: {
          page: currentPage,
          limit: 10
        }
      }
    });
  }, [currentPage]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatStatus = (status: string) => {
    if (!status) return '';
    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getChipColor = (status: RingMyPhoneStatus) => {
    switch (status) {
      case RingMyPhoneStatus.Completed:
        return greenOne;
      case RingMyPhoneStatus.Failed:
        return red;
      default:
        return blue;
    }
  };

  const loading = logLoading;
  return (
    <Box marginTop="16px">
      <Box sx={[userDetailsWrapper, { minWidth: '70%' }]} marginBottom="24px">
        <Typography variant="h6" sx={{ color: 'blackOne', fontWeight: '700', pb: '24px' }}>
          {withTrademark(LABEL_RING_MY_PHONE_LOGS)}
        </Typography>
        {selectedPlan?.nickname === 'Free plan' ? (
          <CardLockComponents />
        ) : (
          <Box>
            <StickyTable height="auto" pagination={<CustomPagination loading={loading} />}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ color: blackTwo }}>
                      {LABEL_DATE_AND_TIME}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ color: blackTwo, whiteSpace: 'nowrap' }}>
                      {LABEL_NOTIFICATION_TYPE}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ color: blackTwo, whiteSpace: 'nowrap' }}>
                      {LABEL_CALL_STATUS}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ color: blackTwo }}>
                      {LABEL_LOCATION}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ color: blackTwo, whiteSpace: 'nowrap' }}>
                      {LABEL_CALL_CHARGES}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ color: blackTwo }}>
                      {LABEL_STATUS}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {logs?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" sx={{ color: blackOne }}>
                          {MSG_NO_RMP_LOGS_FOUND}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableBody>
                      {logs?.map((log, ind) => {
                        const { id, notificationType, ringMyPhoneStatus, status, createdAt, longitude, latitude, price } = log;
                        return (
                          <TableRow key={id}>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ color: blackOne, whiteSpace: 'nowrap' }}>
                                {new Date(createdAt).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="subtitle2" sx={{ color: blackOne }}>
                                {formatStatus(notificationType ?? '')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ color: blackOne }}>
                                {status ? formatStatus(status) : LABEL_NOT_AVAILABLE}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {latitude && longitude ? (
                                <Tooltip title={BUTTON_TEXT_VIEW_ON_MAP}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    startIcon={<RoomIcon sx={{ fontSize: 18 }} />} // smaller icon
                                    onClick={() => {
                                      setSelectedIndex(ind);
                                      setOpen(true);
                                    }}
                                    sx={{
                                      textTransform: 'none',
                                      borderRadius: 2,
                                      px: 1.2,
                                      py: 0.4,
                                      fontSize: '0.8rem',
                                      minWidth: 'unset', // keeps it compact
                                      display: 'flex',
                                      alignItems: 'center'
                                    }}
                                  >
                                    {BUTTON_TEXT_VIEW}
                                  </Button>
                                </Tooltip>
                              ) : (
                                <Typography variant="subtitle2" sx={{ color: blackOne }}>
                                  {LABEL_NOT_AVAILABLE}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight={700}>
                                {price ? `$ ${price}` : '$ 0'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ color: blackOne }}>
                                {ringMyPhoneStatus ? <TransactionsHistoryChipStyle size="small" label={formatStatus(ringMyPhoneStatus ?? '')} customcolor={getChipColor(ringMyPhoneStatus)} /> : LABEL_NOT_AVAILABLE}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  )}
                </>
              )}
            </StickyTable>
          </Box>
        )}
      </Box>

      {selectedIndex != null && logs[selectedIndex]?.latitude && logs[selectedIndex]?.longitude && (
        <LocationModal
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          latitude={logs[selectedIndex].latitude || ''}
          longitude={logs[selectedIndex].longitude || ''}
          phone={(logs[selectedIndex] as any).phone || null}
          createdAt={logs[selectedIndex].createdAt || undefined}
        />
      )}
    </Box>
  );
};

export default RingMyPhoneLogs;
